import React, { useState, useEffect, useCallback } from 'react';
import { printShippingLabel } from '../../utils/printShippingLabel';
import { downloadInvoice } from '../../utils/downloadInvoice';
import { useEmailPreview, EmailPreviewModal } from './EmailPreviewModal';

/* ===== ADMIN DASHBOARD ===== */
const STATUS_LABELS = {
  pending:   { label: 'Pending',   color: '#e67e22' },
  confirmed: { label: 'Confirmed', color: '#2980b9' },
  shipped:   { label: 'Shipped',   color: '#8e44ad' },
  delivered: { label: 'Delivered', color: '#27ae60' },
  cancelled: { label: 'Cancelled', color: '#c0392b' },
};
const ALL_STATUSES = ['all', 'pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];

export function AdminDashboard({ token, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const { emailPreview, setEmailPreview, openEmailPreview, sendEmailFromPreview } = useEmailPreview(token);

  const fetchOrders = useCallback(async (status = 'all') => {
    setLoading(true);
    setFetchError('');
    try {
      const url = status === 'all'
        ? '/.netlify/functions/get-orders'
        : `/.netlify/functions/get-orders?status=${status}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) {
        setFetchError(`Error ${res.status}: ${data.error || 'Failed to load orders'}`);
        setOrders([]);
      } else {
        setOrders(data.orders || []);
      }
    } catch (err) {
      setFetchError(`Network error: ${err.message}`);
      setOrders([]);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchOrders(filter); }, [filter, fetchOrders]);

  async function updateStatus(orderId, status) {
    setUpdatingId(orderId);
    await fetch('/.netlify/functions/update-order-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orderId, status }),
    });
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    setUpdatingId(null);
  }

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const totalRevenue = orders
    .filter(o => o.payment_status === 'paid')
    .reduce((s, o) => s + o.total, 0);

  return (
    <div className="admin-wrap">
      <div className="admin-header">
        <div className="admin-header-left">
          <img src="/logo.webp" alt="Kiane" className="admin-logo-sm" />
          <h1>Admin Dashboard</h1>
        </div>
        <button className="admin-logout-btn" onClick={onLogout}>Logout</button>
      </div>

      <div className="admin-stats">
        <div className="admin-stat-card">
          <span className="stat-num">{orders.length}</span>
          <span className="stat-label">Total Orders</span>
        </div>
        <div className="admin-stat-card">
          <span className="stat-num">₹{totalRevenue.toLocaleString('en-IN')}</span>
          <span className="stat-label">Revenue Collected</span>
        </div>
        <div className="admin-stat-card">
          <span className="stat-num" style={{ color: '#e67e22' }}>{counts.pending || 0}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="admin-stat-card">
          <span className="stat-num" style={{ color: '#27ae60' }}>{counts.delivered || 0}</span>
          <span className="stat-label">Delivered</span>
        </div>
      </div>

      <div className="admin-filters">
        {ALL_STATUSES.map(s => (
          <button
            key={s}
            className={`admin-filter-btn${filter === s ? ' active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'All' : STATUS_LABELS[s].label}
          </button>
        ))}
        <button className="admin-refresh-btn" onClick={() => fetchOrders(filter)}>↻ Refresh</button>
      </div>

      {loading ? (
        <div className="admin-loading">Loading orders…</div>
      ) : fetchError ? (
        <div className="admin-empty" style={{ color: '#c0392b' }}>⚠ {fetchError}</div>
      ) : orders.length === 0 ? (
        <div className="admin-empty">No orders found.</div>
      ) : (
        <div className="admin-orders">
          {orders.map(order => {
            const isExpanded = expandedId === order.id;
            const s = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
            const date = new Date(order.created_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            });
            return (
              <div key={order.id} className={`admin-order-card${isExpanded ? ' expanded' : ''}`}>
                <div className="admin-order-row" onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                  <div className="admin-order-meta">
                    <code className="admin-order-id">#{order.id.substring(0, 8).toUpperCase()}</code>
                    <span className="admin-order-date">{date}</span>
                  </div>
                  <div className="admin-order-customer">
                    <strong>{order.customer_name}</strong>
                    <span>{order.customer_phone}</span>
                  </div>
                  <div className="admin-order-amount">₹{order.total}</div>
                  <span className="admin-status-badge" style={{ background: s.color }}>{s.label}</span>
                  <span className={`admin-payment-badge${order.payment_status === 'paid' ? ' paid' : ''}`}>
                    {order.payment_status === 'paid' ? '✓ Paid' : '⏳ Pending'}
                  </span>
                  {order.payment_status === 'paid' && (
                    <button
                      className="admin-download-btn"
                      title="Download Invoice"
                      onClick={e => { e.stopPropagation(); downloadInvoice(order); }}
                    >
                      ↓ Invoice
                    </button>
                  )}
                  <button
                    className="admin-label-btn"
                    title="Print shipping label"
                    onClick={e => { e.stopPropagation(); printShippingLabel(order); }}
                  >
                    ⎙ Label
                  </button>
                  {order.customer_email && (
                    <button
                      className="admin-email-btn"
                      title={`Preview & send ${order.status} email to ${order.customer_email}`}
                      onClick={e => { e.stopPropagation(); openEmailPreview(order); }}
                    >
                      ✉ Email
                    </button>
                  )}
                  <span className="admin-chevron">{isExpanded ? '▲' : '▼'}</span>
                </div>

                {isExpanded && (
                  <div className="admin-order-detail">
                    <div className="admin-detail-grid">
                      <div className="admin-detail-section">
                        <h4>Customer Details</h4>
                        <p><label>Name</label><span>{order.customer_name}</span></p>
                        <p><label>Phone</label><a href={`tel:${order.customer_phone}`}>{order.customer_phone}</a></p>
                        <p><label>Email</label><span>{order.customer_email || '—'}</span></p>
                      </div>
                      <div className="admin-detail-section">
                        <h4>Delivery Address</h4>
                        <p><label>Address</label><span>{order.address}</span></p>
                        <p><label>City</label><span>{order.city || '—'}</span></p>
                        <p><label>State</label><span>{order.state || '—'}</span></p>
                        <p><label>Pincode</label><span>{order.pincode || '—'}</span></p>
                      </div>
                      <div className="admin-detail-section">
                        <h4>Order Items</h4>
                        {(order.items || []).map((item, idx) => (
                          <p key={idx}>
                            <label>{item.name}</label>
                            <span>Qty: {item.qty || item.quantity} × ₹{item.price} = ₹{item.price * (item.qty || item.quantity)}</span>
                          </p>
                        ))}
                        <p className="admin-total-row"><label>Subtotal</label><span>₹{order.subtotal}</span></p>
                        <p className="admin-total-row"><label><strong>Total</strong></label><span><strong>₹{order.total}</strong></span></p>
                      </div>
                      <div className="admin-detail-section">
                        <h4>Update Status</h4>
                        <div className="admin-status-btns">
                          {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => {
                            const isPending = key === 'pending';
                            const orderIsBeyondPending = ['confirmed','shipped','delivered','cancelled'].includes(order.status);
                            if (isPending && orderIsBeyondPending) return null;
                            return (
                              <button
                                key={key}
                                className={`admin-status-opt${order.status === key ? ' current' : ''}`}
                                style={order.status === key ? { background: color, color: '#fff' } : {}}
                                disabled={updatingId === order.id}
                                onClick={() => updateStatus(order.id, key)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="admin-order-id-full"><label>Order ID</label><code>{order.id}</code></p>
                        <p><label>Gateway ID</label><code>{order.gateway_payment_id || '—'}</code></p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Email Preview Modal ── */}
      <EmailPreviewModal
        emailPreview={emailPreview}
        setEmailPreview={setEmailPreview}
        sendEmailFromPreview={sendEmailFromPreview}
        statusLabels={STATUS_LABELS}
      />
    </div>
  );
}
